// BUILD-046 (Owner direction 2026-10-20, OWNER-APPROVED - BUILDER RECONCILIATION REQUIRED): the ONE list of supported task priorities.
//
// "Use the same supported priority choices in Assign and Edit, including the existing Urgent option. New assignments default to Medium. Existing unset
// priorities remain unset." The Assign dialog (assignModel.ASSIGN_PRIORITY_OPTIONS) and the Edit form (editPriorityModel.EDIT_PRIORITIES / editPriorityOptions)
// both derive from this module, so the two can never offer different choices. The stored values are lowercase `low | medium | high | urgent` (the server
// accepts exactly these at POST /work/assign and PUT /work/{id}); the labels are what the person reads.
//
// This module holds only the choices. Whether a task has a saved priority, and "Not set" for a task without one, belong to the Edit form
// (editPriorityModel.ts): the Assign dialog never shows "Not set" because a new assignment always has a priority (Medium until changed).
//
// Pure module: no React, no I/O, no imports. Erasable TypeScript only.

export type PriorityChoice = 'low' | 'medium' | 'high' | 'urgent';

/** The supported priorities in display order (lowest first). */
export const PRIORITY_CHOICES: readonly PriorityChoice[] = Object.freeze(['low', 'medium', 'high', 'urgent'] as PriorityChoice[]);

/** What each priority is called on screen. */
export const PRIORITY_LABELS: Readonly<Record<PriorityChoice, string>> = Object.freeze({ low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent' });

/** What a new assignment starts on. */
export const DEFAULT_PRIORITY: PriorityChoice = 'medium';

/** The options of a priority select, in order: Low, Medium, High, Urgent. */
export const PRIORITY_OPTIONS: readonly { value: PriorityChoice; label: string }[] = Object.freeze(PRIORITY_CHOICES.map((value) => Object.freeze({ value, label: PRIORITY_LABELS[value] })));

/** True when `value` is exactly one of the four supported priorities (lowercase, no trimming, no case folding). */
export function isPriorityChoice(value: unknown): value is PriorityChoice {
  return typeof value === 'string' && (PRIORITY_CHOICES as readonly string[]).includes(value);
}
